// pages/WaitlistedAppointments.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  listWaitlistedAppointmentsByDate,
  removeFromWaitlist,
} from "../APIs/waitlistApi";

// ——— helpers ———
const ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Phoenix",
  "America/Toronto",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Kolkata",
  "Asia/Tokyo",
];

const defaultZone = (() => {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
    );
  } catch {
    return "America/New_York";
  }
})();

function todayYYYYMMDD(zoneId = defaultZone) {
  // keep simple: use local browser date
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fmtTime(zdtLike) {
  if (!zdtLike) return "";
  const iso = String(zdtLike).split("[")[0]; // handle …[America/New_York]
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export default function PatientWaitlistTab({ patientId: patientIdProp }) {
  // Get patientId from route or prop (your route could be: /patient/:patientId/waitlist)
  const { patientId: patientIdParam } = useParams();
  const patientId = patientIdProp ?? patientIdParam;

  const [date, setDate] = useState(() => todayYYYYMMDD());
  const [zone, setZone] = useState(defaultZone);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]); // array of WaitlistDto

  const fetchList = async () => {
    if (!patientId) return;
    setLoading(true);
    setErr("");
    try {
      const data = await listWaitlistedAppointmentsByDate(Number(patientId), {
        date,
        zone,
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.message || "Failed to load waitlisted appointments");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, date, zone]);

  const handleRemove = async (waitlistId) => {
    if (!window.confirm("Remove this waitlist entry?")) return;
    try {
      await removeFromWaitlist(Number(patientId), Number(waitlistId));
      // refresh list
      fetchList();
    } catch (e) {
      alert(e?.message || "Failed to remove waitlist entry");
    }
  };

  return (
    <div className="max-w-3xl p-4 space-y-4">
      <h3 className="text-xl font-semibold">My Waitlisted Appointments</h3>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block mb-1 font-medium">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border rounded px-3 py-2"
          />
        </div>
        <div>
          <label className="block mb-1 font-medium">Time Zone</label>
          <select
            value={zone}
            onChange={(e) => setZone(e.target.value)}
            className="w-full border rounded px-3 py-2"
          >
            {ZONES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={fetchList}
            className="w-full px-4 py-2 rounded bg-gray-800 text-white hover:bg-black"
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Results */}
      <div className="min-h-[120px]">
        {loading && <div className="text-gray-600">Loading…</div>}
        {err && !loading && <div className="text-red-600">{err}</div>}

        {!loading && !err && (
          <div className="grid gap-3">
            {items.length === 0 ? (
              <div className="text-gray-500">
                No waitlisted appointments for this date.
              </div>
            ) : (
              items.map((wl) => {
                const start = wl.appointmentStart;
                const end = wl.appointmentEnd;
                // Fallback: if only start exists, you can omit end or compute if duration known.
                const startStr = fmtTime(start);
                const endStr = end ? fmtTime(end) : "";

                return (
                  <div
                    key={wl.waitlistId}
                    className="w-full bg-white border rounded-2xl shadow-sm p-4"
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="text-lg font-semibold">
                          {wl.providerName || "Provider"}
                        </div>
                        {wl.providerSpecialties && (
                          <div className="text-sm text-gray-600">
                            {wl.providerSpecialties}
                          </div>
                        )}
                        <div className="mt-1 text-sm text-gray-700">
                          <span className="font-medium">Start:</span> {startStr}
                          {endStr ? (
                            <>
                              {" "}
                              <span className="font-medium ml-3">
                                End:
                              </span>{" "}
                              {endStr}
                            </>
                          ) : null}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2 py-1 rounded text-xs font-semibold ${
                            wl.waitlistState === "PENDING"
                              ? "bg-yellow-100 text-yellow-700"
                              : wl.waitlistState === "NOTIFIED"
                                ? "bg-blue-100 text-blue-700"
                                : wl.waitlistState === "ACCEPTED"
                                  ? "bg-green-100 text-green-700"
                                  : wl.waitlistState === "DECLINED"
                                    ? "bg-gray-200 text-gray-700"
                                    : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {wl.waitlistState}
                        </span>

                        <button
                          onClick={() => handleRemove(wl.waitlistId)}
                          className="px-3 py-1.5 rounded-lg text-sm bg-red-600 text-white hover:bg-red-700"
                          title="Remove from waitlist"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}
